import {
  Account,
  apiAbortController,
  BaseTransaction,
  Response,
} from '@ui/src';
import api from '../../../api/api';
import {
  Fintrac,
  FintracReportFormValues,
  FintracTransaction,
  PaymentType,
} from '../types';

export const fintracApi = api.injectEndpoints({
  endpoints: (build) => ({
    getFintracReports: build.query<Response<Fintrac>, string>({
      query: (query) => ({
        url: `/v1/fintrac-reports${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getFintracReport: build.query<{ data: Fintrac }, string>({
      query: (id) => ({
        url: `/v1/fintrac-reports/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getFintracIncomingTransactions: build.query<
      Response<BaseTransaction>,
      string
    >({
      query: (query) => ({
        url: `/v1/transactions-incoming${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getFintracTransactionsAccounts: build.query<Response<Account>, string>({
      query: (query) => ({
        url: `/v1/accounts${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getFintracReportTransactions: build.query<
      Response<FintracTransaction>,
      string
    >({
      query: (query) => ({
        url: `/v1/fintrac-transactions${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getFintracPaymentTypes: build.query<Response<PaymentType>, void>({
      query: () => ({
        url: '/v1/payment-categories?all=true',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    createFintracReport: build.mutation<
      { data: Fintrac },
      FintracReportFormValues
    >({
      query: (body) => ({
        url: '/v1/fintrac-reports',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deleteFintracReport: build.mutation<{ data: Fintrac }, string>({
      query: (id) => ({
        url: `/v1/fintrac-reports/${id}`,
        method: 'DELETE',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    selectTransactionInReport: build.mutation<
      { data: FintracTransaction },
      {
        id: string;
        isSelectedForReport: boolean;
      }
    >({
      query: (data) => ({
        url: `/v1/fintrac-transactions/${data.id}`,
        method: 'PUT',
        body: {
          isSelectedForReport: data.isSelectedForReport,
        },
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    deselectAllTransactionInReport: build.mutation<{ count: number }, string>({
      query: (id) => ({
        url: `/v1/fintrac-transactions/${id}/deselect-all`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    selectAllTransactionInReport: build.mutation<{ count: number }, string>({
      query: (id) => ({
        url: `/v1/fintrac-transactions/${id}/select-all`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    submitReport: build.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/v1/fintrac-reports/${id}/send`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    cancelReport: build.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/v1/fintrac-reports/${id}/cancel`,
        method: 'POST',
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    addTransactionsToFintracReport: build.mutation<
      any,
      { items: { regulatoryReportId: string; transactionId: string }[] }
    >({
      query: (body) => ({
        url: '/v1/fintrac-transactions/batch-create',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    downloadReport: build.query<{ data: any }, string>({
      query: (id) => ({
        url: `/v1/uploads/${id}/file`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetFintracIncomingTransactionsQuery,
  useLazyGetFintracTransactionsAccountsQuery,
  useLazyGetFintracPaymentTypesQuery,
  useLazyGetFintracReportsQuery,
  useCreateFintracReportMutation,
  useAddTransactionsToFintracReportMutation,
  useLazyGetFintracReportTransactionsQuery,
  useSelectTransactionInReportMutation,
  useDeselectAllTransactionInReportMutation,
  useSelectAllTransactionInReportMutation,
  useLazyGetFintracReportQuery,
  useDeleteFintracReportMutation,
  useSubmitReportMutation,
  useCancelReportMutation,
  useLazyDownloadReportQuery,
} = fintracApi;

export const {
  endpoints: {
    getFintracIncomingTransactions,
    getFintracTransactionsAccounts,
    getFintracPaymentTypes,
    getFintracReports,
    createFintracReport,
    addTransactionsToFintracReport,
    getFintracReportTransactions,
    selectTransactionInReport,
    deselectAllTransactionInReport,
    selectAllTransactionInReport,
    getFintracReport,
    deleteFintracReport,
    cancelReport,
    submitReport,
    downloadReport,
  },
} = fintracApi;
